/**
 * Local-timezone date helpers for the `yyyy-MM-dd` strings the schedule API speaks.
 *
 * Everything here is deliberately local-time. `new Date('2026-07-30')` parses as UTC,
 * which lands on the 29th for anyone west of Greenwich — the schedule would show the
 * wrong day for most of the country. Always go through `parseIsoDate` / `toIsoDate`.
 */

/** `yyyy-MM-dd` for a Date, in the browser's local timezone. */
export function toIsoDate(d: Date): string {
  const m = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

/** Parse `yyyy-MM-dd` as a local midnight Date. */
export function parseIsoDate(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, (m ?? 1) - 1, d ?? 1);
}

/** Today as `yyyy-MM-dd`, local. */
export function todayIso(): string {
  return toIsoDate(new Date());
}

/** Shift a `yyyy-MM-dd` string by whole days (negative goes back). DST-safe. */
export function addDays(iso: string, days: number): string {
  const d = parseIsoDate(iso);
  d.setDate(d.getDate() + days);
  return toIsoDate(d);
}

/** Whole days from `a` to `b`, both `yyyy-MM-dd`. Positive when `b` is later. */
export function daysBetween(a: string, b: string): number {
  return Math.round((parseIsoDate(b).getTime() - parseIsoDate(a).getTime()) / 86_400_000);
}

/**
 * Inclusive calendar-day span of a `yyyy-MM-dd` range. A one-day assignment is 1, not 0.
 */
export function spanDays(startIso: string, endIso: string): number {
  return daysBetween(startIso, endIso) + 1;
}

/** `Jul 30` — the compact form used inside date ranges. */
export function formatShort(iso: string): string {
  return parseIsoDate(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** `Thu, Jul 30` — the form used in day headers and standalone single-day labels. */
export function formatShortWeekday(iso: string): string {
  return parseIsoDate(iso).toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

/** `Jul 15, 2025` — the compact form when a date falls outside the current year. */
export function formatShortWithYear(iso: string): string {
  return parseIsoDate(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/** `July 30, 2026` — banners and screen-reader labels only, never a dense list. */
export function formatLong(iso: string): string {
  return parseIsoDate(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/** `July 30` — the year-less long form, for screen-reader date ranges. */
export function formatLongDayMonth(iso: string): string {
  return parseIsoDate(iso).toLocaleDateString(undefined, { month: 'long', day: 'numeric' });
}

/** `Thursday, July 30` — spoken day names for `aria-label`s on the day navigator. */
export function formatLongWeekday(iso: string): string {
  return parseIsoDate(iso).toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
}
